import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { TFormBuilder } from '@ppl/ui/form';
import { PASSWORD_MAX_LENGTH, passwordPatternMatcher } from '@ppl/utils/password.utils';
import type {
  AbstractControlOptions, ValidationErrors
} from '@angular/forms';
import type { TControlsConfig, TFormGroup } from '@ppl/ui/form';

@Component({
    selector: 'ppl-password-change',
    templateUrl: './password-change.component.html',
    styleUrls: ['./password-change.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PasswordChangeComponent {

  @Input() form: UntypedFormGroup;
  @Input() canChangePassword: boolean;
  @Input() enabled = false;

  @Output() changePassword = new EventEmitter();

  static createChangePasswordForm(): TFormGroup<PasswordChangeFormData> {
    const fb = new TFormBuilder();
    const [controlsConfig, options] = this.createPasswordFormConfig();
    return fb.group<PasswordChangeFormData>(
      { ...controlsConfig, currentPassword: ['', Validators.required] },
      options
    );
  }

  static createPasswordForm(): TFormGroup<PasswordFormData> {
    const fb = new TFormBuilder();
    const [controlsConfig, options] = this.createPasswordFormConfig();
    return fb.group<PasswordFormData>(controlsConfig, options);
  }

  static createPasswordFormConfig(): [TControlsConfig<PasswordFormData>, AbstractControlOptions] {
    return [
      {
        password1: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(PASSWORD_MAX_LENGTH), passwordPatternMatcher]],
        password2: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(PASSWORD_MAX_LENGTH), passwordPatternMatcher]]
      },
      {
        validators: [passwordsMatchValidator]
      }
    ];
  }
}

export interface PasswordFormData {
  password1: string;
  password2: string;
}

export interface PasswordChangeFormData extends PasswordFormData {
  currentPassword: string;
}

export function passwordsMatchValidator(control: UntypedFormGroup): ValidationErrors | null {
  const errorKey = 'passwords-do-not-match';
  const password1 = control.get('password1');
  const password2 = control.get('password2');
  // NOTE(mike): Reset passwords-do-not-match error on password2 field
  if (password2.errors && errorKey in password2.errors) {
    const { [errorKey]: error, ...rest } = password2.errors;
    const errors = Object.keys(rest).length ? rest : null;
    password2.setErrors(errors);
  }

  if (!password1.errors && !password2.errors) {
    if (password1.value !== password2.value) {
      password2.setErrors({ [errorKey]: 'Passwords don\'t match' });
    }
  }

  return null;
}

